import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { DealerSetup } from '../../features/setup-screens/models/dealer-setup.model';
import {
  ApiResponseCronJobDetail,
  ApiResponseCronJobSummaryList,
  ApiResponseString,
  ApiResponseTriggerJob,
  SuspendRequest,
  UpdateScheduleRequest
} from './models/KubeJobs';

@Injectable({
  providedIn: 'root'
})
export class KubeJobsService {
  private readonly http = inject(HttpClient);

  private readonly IMPORT_JOB = 'pos-master-data-in';
  private readonly EXPORT_JOB = 'pos-master-data-out';
  private readonly BTD_EXPORT_JOB = 'btd-master-data-out';

  listCronJobs(): Observable<ApiResponseCronJobSummaryList> {
    console.log('Fetching cron job summaries from back office', environment);
    const url = `${environment.backOfficeUrl}${environment.serviceUris.kubejobs}`;
    return this.http.get<ApiResponseCronJobSummaryList>(url);
  }

  getCronJobDetail(cronJobName: string): Observable<ApiResponseCronJobDetail> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.kubejobDetail.replace('{cronJobName}', cronJobName)}`;
    return this.http.get<ApiResponseCronJobDetail>(url);
  }

  triggerJob(cronJobName: string): Observable<ApiResponseTriggerJob> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.kubejobTrigger.replace('{cronJobName}', cronJobName)}`;
    return this.http.post<ApiResponseTriggerJob>(url, {});
  }

  restartJob(cronJobName: string): Observable<ApiResponseTriggerJob> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.kubejobRestart.replace('{cronJobName}', cronJobName)}`;
    return this.http.post<ApiResponseTriggerJob>(url, {});
  }

  setSuspended(cronJobName: string, request: SuspendRequest): Observable<ApiResponseString> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.kubejobSuspend.replace('{cronJobName}', cronJobName)}`;
    return this.http.patch<ApiResponseString>(url, request);
  }

  updateSchedule(cronJobName: string, request: UpdateScheduleRequest): Observable<ApiResponseString> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.kubejobSchedule.replace('{cronJobName}', cronJobName)}`;
    return this.http.patch<ApiResponseString>(url, request);
  }

  updateCronSchedules(dealerSetup: DealerSetup): void {
    if (!dealerSetup) return;

    const importCron =
      dealerSetup.posImportOnce === 'Y'
        ? this.buildCronFromStartTime(dealerSetup.posImportStartTime)
        : this.buildCronFromInterval(dealerSetup.posImportTimeInterval);
    this.dispatchCronSchedule(this.IMPORT_JOB, importCron);

    const exportCron = this.buildCronFromInterval(dealerSetup.posExportTimeInterval);
    this.dispatchCronSchedule(this.EXPORT_JOB, exportCron);

    const btdCron =
      dealerSetup.btdExportOnce === 'Y'
        ? this.buildCronFromStartTime(dealerSetup.btdExportStartTime)
        : this.buildCronFromInterval(dealerSetup.btdExportTimeInterval);
    this.dispatchCronSchedule(this.BTD_EXPORT_JOB, btdCron);
  }

  private dispatchCronSchedule(cronJobName: string, cronExpression: string | null): void {
    if (!cronExpression) return;

    this.updateSchedule(cronJobName, { cronExpression }).subscribe();
  }

  private buildCronFromStartTime(startTimeValue: string | Date | null | undefined): string | null {
    if (!startTimeValue) return null;

    const date =
      startTimeValue instanceof Date ? startTimeValue : new Date(startTimeValue.endsWith('Z') ? startTimeValue : `${startTimeValue}Z`);
    if (isNaN(date.getTime())) return null;

    return `${date.getUTCMinutes()} ${date.getUTCHours()} * * *`;
  }

  private buildCronFromInterval(intervalMinutes: number | string | null | undefined): string | null {
    if (intervalMinutes === null || intervalMinutes === undefined || intervalMinutes === '') return null;

    const numericMinutes = Number(intervalMinutes);
    if (!Number.isFinite(numericMinutes) || numericMinutes <= 0) return null;

    return `*/${numericMinutes} * * * *`;
  }

  killJob(cronJobName: string): Observable<void> {
    const url = `${environment.backOfficeUrl}${environment.serviceUris.kubejobKill.replace('{cronJobName}', cronJobName)}`;
    return this.http.delete<void>(url);
  }
}
